import {
  IcArrowRight,
  IcCctvFilled,
  IcCheck,
  IcCheckMarkFilled,
  IcIllustEventAftercardPackage,
  IcInfoCircleOutlined,
  IcSaleMarkFilled,
  IcStarFilled,
} from '@healingpaper/cell-icons'
import { ExtraPriceChargeType, ExtraPriceType } from 'api-client/contracts/service-offer/AdminServiceOfferExtraPrice'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { createContext, useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TimeUnit, type UserServiceOfferDetails, type UserServiceOfferOption } from 'api-client/contracts/service-offer'
import { ServiceOfferPageViewedEvent } from 'web-event-collector/src/interfaces/event/ServiceOfferPageViewed'
import { createFetchWebEventCollector, EventName, PageName, Predecessor } from 'web-event-collector'
import { format, parseISO } from 'date-fns'
import { getEnv } from '@unni/platform'

import { OptionCard, TreatmentItemInline } from './components/option-card'
import Rewards from './components/point-rewards'

import { AirbridgeEvents, sendAirbridgeEvent } from '@/utils/airbridge'
import { cn, getStaticGoogleMapURL } from '@/shared/utils'
import { createSignInUrl } from '@/shared/utils/url-utils'
import { useLocales } from '@/shared/hooks/useLocales'
import FlexRow from '@/components/shared/FlexRow'
import FlexCol from '@/components/shared/FlexCol'
import ReviewTemplate from '@/components/atomic/templates/ReviewTemplate'
import cellTheme from '@/components/cell/designTokens'
import { getPriceWithMonetaryUnit, type MonetaryUnitString } from '@/utils/price'
import { Languages, Locales } from '@/utils/language'
import { getDownloadLink, postAmplitude, postGtag } from '@/core/lib'
import { createCommonEvent } from '@/utils/eventCollector'
import BottomBarInfoButtons from '@/components/atomic/molecules/BottomBarInfoButtons'
import ConfirmPopup from '@/shared/components/confirm-popup'
import { User } from '@/features/member/member.type'
import { ReviewsPaginationResponseInternal } from '@/features/review/api/review.type'

const ServiceOfferContext = createContext<{
  serviceOffer: UserServiceOfferDetails
  representativeOption: UserServiceOfferOption
  user?: User
  reviewWithPagination?: ReviewsPaginationResponseInternal | null
} | null>(null)

const useDetailServiceOffer = () => {
  const context = useContext(ServiceOfferContext)
  if (!context) {
    throw new Error('useDetailServiceOffer must be used within a ServiceOfferProvider')
  }
  return context
}

interface Props {
  serviceOffer: UserServiceOfferDetails
  user?: User
  reviewWithPagination?: ReviewsPaginationResponseInternal | null
  children?: React.ReactNode
}

export const ServiceOfferTemplate = ({ serviceOffer, user, reviewWithPagination, children }: Props) => {
  const optionId = useRouter().query.optionId as string
  const representativeOption = serviceOffer.options.find((option) => option.id === optionId) || serviceOffer.options[0]

  return (
    <ServiceOfferContext.Provider value={{ serviceOffer, representativeOption, user, reviewWithPagination }}>
      <EmitterProvider>{children}</EmitterProvider>
    </ServiceOfferContext.Provider>
  )
}

const EmitContext = createContext<{
  emitServiceOfferViewed: () => void
  emitHospitalClickedEvent: () => void
}>({
  emitServiceOfferViewed: () => {},
  emitHospitalClickedEvent: () => {},
})

const useEmitter = () => {
  const context = useContext(EmitContext)
  if (!context) {
    throw new Error('useEmitter must be used within a EmitProvider')
  }
  return context
}

const EmitterProvider = ({ children }: { children: React.ReactNode }) => {
  const { serviceOffer } = useDetailServiceOffer()
  const [isPageViewedEmitted, setIsPageViewedEmitted] = useState(false)

  const { emitHospitalClicked, emitServiceOfferPageViewed } = createFetchWebEventCollector({
    baseURL: getEnv('NEXT_PUBLIC_SOLAR_API_HOST') || '',
  })

  const emitPageViewed = (userId: number) => {
    const predecessor = localStorage.getItem('predecessor')

    localStorage.removeItem('predecessor')

    const commonEvent = createCommonEvent(navigator.userAgent, userId)

    const pageViewedEvent: ServiceOfferPageViewedEvent = {
      ...commonEvent,
      Name: EventName.ServiceOfferPageViewed,
      Body: {
        ...commonEvent.Body,
        hospitalId: serviceOffer.hospital.id,
        serviceOfferId: serviceOffer.id,
        serviceOfferType: 'SPECIAL_OFFER',
        sellingPriceCurrency: serviceOffer.price.currency,
        sellingPriceAmount: serviceOffer.price.discountedAmount,
        ...(predecessor && { predecessor: JSON.parse(predecessor) }),
        pageName: PageName.EventDetailWeb,
      },
    }

    emitServiceOfferPageViewed(pageViewedEvent)
  }

  const emitServiceOfferViewed = () => {
    const userId = sessionStorage.getItem('user_id')
    if (!userId || isPageViewedEmitted) {
      return
    }

    const timeout = setTimeout(() => emitPageViewed(Number(userId)), 1000)

    setIsPageViewedEmitted(true)

    return () => {
      clearTimeout(timeout)
    }
  }

  function emitHospitalClickedEvent() {
    const userId = sessionStorage.getItem('user_id')
    if (!userId) return

    const commonEvent = createCommonEvent(navigator.userAgent, Number(userId))

    const predecessor: Predecessor = {
      Name: EventName.HospitalClicked,
      TraceId: commonEvent.TraceId,
    }

    localStorage.setItem('predecessor', JSON.stringify(predecessor))

    emitHospitalClicked({
      ...commonEvent,
      Name: EventName.HospitalClicked,
      Body: {
        ...commonEvent.Body,
        hospitalId: serviceOffer.hospital.id,
        pageName: PageName.EventDetailWeb,
        itemOrderInPage: 2,
      },
    })
  }

  return (
    <EmitContext.Provider value={{ emitServiceOfferViewed, emitHospitalClickedEvent }}>{children}</EmitContext.Provider>
  )
}

/* Components */
const CardImage = () => {
  const {
    serviceOffer: { cardImage },
  } = useDetailServiceOffer()

  const { emitHospitalClickedEvent } = useEmitter()

  return (
    <FlexRow className={cn('relative w-full')}>
      <Image
        src={cardImage.url}
        alt="cardImage"
        width={1080}
        height={470}
        onLoad={() => {
          emitHospitalClickedEvent()
        }}
      />
    </FlexRow>
  )
}

const MetaInfoWithRating = () => {
  const { t } = useTranslation()
  const {
    serviceOffer: {
      id,
      hospital: { customersPick },
      title,
      rating,
    },
  } = useDetailServiceOffer()

  const onRatingCountClick = () => {
    postAmplitude('click_review_ATF', {
      event_id: id,
    })

    const target = document.querySelector('#event-review')
    if (!target) return
    const yOffset = -48 // header height
    const yPosition = target.getBoundingClientRect().top + window.scrollY + yOffset

    window.scrollTo({
      top: yPosition,
      behavior: 'smooth',
    })
  }

  return (
    <FlexCol pb={16} gap={8}>
      <FlexRow className={cn(!customersPick && 'hidden')} items="center" gap={4}>
        <IcCheckMarkFilled color={cellTheme.colors.certified} width={16} height={16} />
        <span className="typo-body1Bold text-accent-blueNormal">{t('hospital.certifiedHospital')}</span>
      </FlexRow>

      <FlexRow elementType="h1" className="typo-subTitle3Medium">
        {title}
      </FlexRow>

      <FlexRow hidden={!rating} gap={4} items="center">
        <IcStarFilled width={16} height={16} color={cellTheme.colors.yellow300} />
        <span className="typo-body1Medium">{rating.totalRating.toFixed(1)}</span>
        <button className="typo-body1Regular text-gray-800" type="button" onClick={onRatingCountClick}>
          (<span className="underline">{rating.ratingCount.toLocaleString()}</span>)
        </button>
      </FlexRow>
    </FlexCol>
  )
}

const SelectedOption = () => {
  const { t } = useTranslation()
  const {
    representativeOption,
    serviceOffer: { id, options },
  } = useDetailServiceOffer()

  const index = options?.findIndex((option) => option.id === representativeOption.id) || 0

  const scrollToOption = () => {
    const target = document.getElementById('option-list')
    if (!target) return
    const yOffset = -48 // header height
    const yPosition = target.getBoundingClientRect().top + window.scrollY + yOffset
    window.scrollTo({ top: yPosition, behavior: 'smooth' })
  }

  return (
    <>
      <Divider />
      <FlexRow justify="space-between" items="center" gap={8}>
        <FlexCol className="flex-1 min-w-0" gap={2}>
          <h3 className="typo-body1Medium text-typography-description">
            {t('pages.eventDetail.commonEventOptionNumber', { number: index + 1 })}
          </h3>
          <div
            className={cn(
              'typo-subTitle1Medium text-typography-primary',
              'block w-full',
              'text-ellipsis overflow-hidden whitespace-nowrap',
            )}
          >
            {representativeOption.name}
          </div>
          <TreatmentItemInline treatments={representativeOption.treatments} />
        </FlexCol>
        <FlexCol>
          <button
            className={cn(
              'typo-footer1MediumOld text-typography-primary',
              'px-4 py-2',
              'border border-gray-400 rounded-lg',
            )}
            onClick={() => {
              // track click default option in ATF
              postAmplitude('click_default_option_in_ATF', {
                event_id: id,
              })
              scrollToOption()
            }}
          >
            {t('treatment.viewAll')}
          </button>
        </FlexCol>
      </FlexRow>
    </>
  )
}

interface PriceDisplayProps {
  currency: string
  originalAmount: number
  discountedAmount: number
  discountPercentage: number
  isLocaleJapanese: boolean
}

const SpecialPriceDisplay = ({ currency, discountedAmount, originalAmount, isLocaleJapanese }: PriceDisplayProps) => {
  const { t, i18n } = useTranslation()

  return (
    <FlexCol className="gap-[2px]">
      <FlexRow gap={4} className="typo-subTitle3Bold text-typography-primary">
        <span>{t('common.price.specialPrice')}</span>
        <span>
          {getPriceWithMonetaryUnit({
            currency: currency as MonetaryUnitString,
            locale: i18n.language as Locales,
            price: discountedAmount,
          })}
        </span>
      </FlexRow>
      <FlexRow
        elementType="p"
        className={cn(!isLocaleJapanese && 'line-through', 'typo-body1Regular text-typography-description')}
      >
        {isLocaleJapanese && '通常価格: '}
        {getPriceWithMonetaryUnit({
          currency: currency as MonetaryUnitString,
          locale: i18n.language as Locales,
          price: originalAmount,
        })}
      </FlexRow>
    </FlexCol>
  )
}

const RegularPriceDisplay = ({
  currency,
  originalAmount,
  discountedAmount,
  discountPercentage,
  isLocaleJapanese,
}: PriceDisplayProps) => {
  const { i18n } = useTranslation()

  return (
    <FlexCol>
      {discountPercentage > 0 && (
        <FlexRow gap={4}>
          <FlexCol className="typo-body1Bold text-typography-description">
            {discountPercentage}% {isLocaleJapanese && 'off'}
          </FlexCol>
          <FlexRow
            elementType="p"
            className={cn(!isLocaleJapanese && 'line-through', 'typo-body1Regular text-typography-description')}
          >
            {isLocaleJapanese && '通常価格: '}
            {getPriceWithMonetaryUnit({
              currency: currency as MonetaryUnitString,
              locale: i18n.language as Locales,
              price: originalAmount,
            })}
          </FlexRow>
        </FlexRow>
      )}
      <FlexRow elementType="h2" className="typo-subTitle3Bold text-typography-primary">
        {getPriceWithMonetaryUnit({
          currency: currency as MonetaryUnitString,
          locale: i18n.language as Locales,
          price: discountedAmount,
        })}
      </FlexRow>
    </FlexCol>
  )
}

const Price = () => {
  const { t, i18n } = useTranslation()
  const { representativeOption } = useDetailServiceOffer()
  const price = representativeOption.price.exchangeRateAppliedPrice || representativeOption.price
  const { currency, originalAmount, discountedAmount } = price
  const discountPercentage = representativeOption.price.discountPercentage
  const isDomesticServiceOffer = representativeOption.price.exchangeRateAppliedPrice === null
  const isLocaleJapanese = (i18n.language as Locales) === Locales.JA_JP

  if (!discountedAmount) return null

  const priceProps: PriceDisplayProps = {
    currency,
    originalAmount,
    discountedAmount,
    discountPercentage,
    isLocaleJapanese,
  }

  return (
    <>
      <Divider />
      <FlexCol gap={8}>
        {/* @link: https://healingpaper.slack.com/archives/C014M6MLWSD/p1761545873680459 */}
        {/* @desc: 일본에서는 50% 이상 할인되는 이벤트들에 대해 % 비율을 노출하지 않아야함 */}
        {/* @desc: 한국에서는 50%이상 할인된 이벤트는 등록이 불가능함. */}
        {discountPercentage >= 50 ? <SpecialPriceDisplay {...priceProps} /> : <RegularPriceDisplay {...priceProps} />}
        <FlexRow className={cn(isDomesticServiceOffer && 'hidden', 'typo-footer1Regular text-typography-description')}>
          {t('pages.eventDetail.eventDetailCurrencyExchangeInfo', {
            targetCurrency: currency,
            priceWithSymbol: getPriceWithMonetaryUnit({
              currency: representativeOption.price.currency as MonetaryUnitString,
              locale: i18n.language as Locales,
              price: representativeOption.price.discountedAmount,
            }),
          })}
        </FlexRow>
      </FlexCol>
    </>
  )
}

const PointRewards = () => {
  const {
    serviceOffer: { id, rewards },
  } = useDetailServiceOffer()

  if (!rewards) return null

  const trackClickPointRewards = () => {
    postAmplitude('click_point_dropdown', {
      event_id: id,
    })
  }

  return (
    <>
      <Divider />
      <Rewards
        rewards={rewards}
        onTitleClick={() => {
          trackClickPointRewards()
        }}
      />
    </>
  )
}

const SurgicalRewards = () => {
  const { t, i18n } = useTranslation()
  const {
    serviceOffer: { surgicalRewardExist },
  } = useDetailServiceOffer()

  if (!surgicalRewardExist) return null

  const url =
    (i18n.language as Locales) === Locales.KO_KR
      ? 'https://kuiperbelt-post.gangnamunni.com/post/196'
      : 'https://kuiperbelt-post.gangnamunni.com/post/208'

  return (
    <FlexRow justify="space-between" items="center">
      <FlexRow gap={8} items="center" full>
        <IcIllustEventAftercardPackage width={24} height={24} />
        <span className="typo-subTitle1Bold text-typography-primary">
          {/* 10만원 상당의 붓기패키지 지급 */}
          {t('pages.eventDetail.eventDetailPackageRewardMessage')}
        </span>
      </FlexRow>
      <Link href={url}>
        <IcInfoCircleOutlined width={20} height={20} color={cellTheme.colors.gray500} />
      </Link>
    </FlexRow>
  )
}

const ExclusivePrice = () => {
  const {
    serviceOffer: {
      pricePolicy: { exclusivePrice },
    },
  } = useDetailServiceOffer()
  const { t } = useTranslation()

  if (!exclusivePrice) return null

  return (
    <FlexRow gap={8} items="center" full>
      <IcSaleMarkFilled width={24} height={24} color={cellTheme.colors.orange500} />
      <span className="typo-subTitle1Bold text-typography-primary">
        {/* 강남언니 한정가격이에요 */}
        {t('pages.eventDetail.eventDetailExclusiveMessage')}
      </span>
    </FlexRow>
  )
}

const EventPeriod = () => {
  const {
    serviceOffer: { period },
  } = useDetailServiceOffer()
  const { t } = useTranslation()

  const formattedPeriod = useMemo(() => {
    if (!period) return null
    if (!period.endsAt) return null

    try {
      const startDate = format(parseISO(period.startsAt), 'yyyy.MM.dd')
      const endDate = format(parseISO(period.endsAt), 'yyyy.MM.dd')

      return `${startDate} ~ ${endDate}`
    } catch (error) {
      console.error(error)
      return null
    }
  }, [period])

  if (!formattedPeriod) return null

  return (
    <>
      <Divider />
      <FlexRow items="center" gap={12}>
        <span className="typo-body1Medium text-gray-800 w-[80px]">
          {/* 이벤트 기간 */}
          {t('pages.eventDetail.eventDetailPeriod')}
        </span>
        <p className="typo-body1Medium text-typography-primary">{formattedPeriod}</p>
      </FlexRow>
    </>
  )
}

const HospitalInfo = () => {
  const {
    serviceOffer: { id, hospital },
  } = useDetailServiceOffer()
  const { emitHospitalClickedEvent } = useEmitter()

  if (!hospital) return null

  const onClickHospital = () => {
    emitHospitalClickedEvent()
    postAmplitude('click_hospital_info', {
      button_position: 'event-top',
      event_id: id,
      hospital_id: hospital.id,
      hospital_name: hospital.name || '',
      hospital_country: hospital.country || '',
    })
  }

  return (
    <FlexCol className="bg-white" px={16} py={24} gap={8}>
      <Link href={`/hospitals/${hospital.id}`} onClick={onClickHospital}>
        <FlexRow elementType="h2" items="center" gap={2}>
          <span className="typo-subTitle3Bold text-typography-primary">{hospital.name}</span>
          <IcArrowRight />
        </FlexRow>
      </Link>
      <FlexRow items="center" gap={8}>
        <span className="typo-body1Medium text-typography-secondary">{hospital?.location?.district}</span>
        {hospital.cctvInstalled && (
          <>
            <Ellipse />
            <FlexRow gap={8} items="center">
              <IcCctvFilled width={20} height={20} color="#444444" />
              <span className="typo-body1Medium text-typography-secondary">CCTV</span>
            </FlexRow>
          </>
        )}
      </FlexRow>
    </FlexCol>
  )
}

const Review = () => {
  const {
    serviceOffer: { id, rating, title, hospital },
    reviewWithPagination,
    user,
  } = useDetailServiceOffer()
  const { countryPathnamePrefix, locale } = useLocales()
  const { t } = useTranslation()
  if (!rating || rating.ratingCount === 0) return null

  const eventProperties = {
    event_id: id,
    event_title: title,
    hospital_id: hospital.id,
    hospital_name: hospital.name,
    hospital_region: hospital.location?.district,
  }

  return (
    <FlexCol id="event-review" className="bg-white" px={16} py={24} gap={8}>
      <ReviewTemplate
        isUser={locale === Locales.JA_JP || (!!user && user.id !== 0)}
        relClickMore="nofollow"
        renderCustomTitle={() => (
          <FlexRow items="center" gap={4}>
            <IcStarFilled width={16} height={16} color={cellTheme.colors.yellow300} />
            <FlexRow gap={8} elementType="h2" items="center">
              <span className="typo-subTitle3Bold text-typography-primary">{rating.totalRating.toFixed(1)}</span>
              <Ellipse />
              <span className="typo-subTitle3Medium text-typography-primary">
                {t('review.reviewTotalCount', {
                  number: rating.ratingCount >= 10000 ? '9,999+' : rating.ratingCount.toLocaleString(),
                })}
              </span>
            </FlexRow>
          </FlexRow>
        )}
        eventId={id}
        reviews={reviewWithPagination?.contents}
        onClickLoginButton={() => {
          sendAirbridgeEvent(AirbridgeEvents.SIGN_UP_BUTTON_CLICK_IN_WEB, {})
          postAmplitude('click_login_review_module_in_event_detail', eventProperties)
        }}
        onClickReviewCard={() => {
          sendAirbridgeEvent(AirbridgeEvents.REVIEW_EVENTPAGE_VIEW, {
            semanticAttributes: {
              products: [
                {
                  productID: id,
                  name: title,
                },
              ],
            },
          })
        }}
        urlClickMore={`${countryPathnamePrefix}/reviews?eventId=${id}`}
        onClickMoreReview={() => {
          postAmplitude('click_more_review_in_event_detail', eventProperties)
        }}
      />
    </FlexCol>
  )
}

const Description = () => {
  const {
    serviceOffer: { description },
  } = useDetailServiceOffer()
  const { t } = useTranslation()

  if (!description) return null

  return (
    <FlexCol>
      <div className="flex flex-col gap-4 flex-wrap">
        <h2 className="typo-subTitle3Bold text-typography-primary">
          {/* 시술 설명 */}
          {t('pages.eventDetail.eventDetailProcedureDescriptionTitle')}
        </h2>
        <pre className="w-full typo-description1 text-gray900 break-words whitespace-pre-line">{description}</pre>
      </div>
    </FlexCol>
  )
}

const Options = () => {
  const {
    serviceOffer: {
      extraPrices,
      pricePolicy: { includeVat },
      options,
    },
  } = useDetailServiceOffer()
  const { t } = useTranslation()
  if (!options || options.length === 0) return null

  const isIncludeVat = includeVat
  const isAnesthesiaCostIncluded =
    extraPrices.find((it) => it.type === ExtraPriceType.ANESTHESIA)?.chargeType === ExtraPriceChargeType.INCLUDED
  const isFollowUpServiceCostIncluded =
    extraPrices.find((it) => it.type === ExtraPriceType.AFTERCARE)?.chargeType === ExtraPriceChargeType.INCLUDED

  return (
    <FlexCol gap={24} id="option-list">
      <FlexCol gap={16}>
        <h2 className="typo-subTitle3Bold text-typography-primary">
          {/* 옵션 정보 */}
          {t('pages.eventDetail.eventDetailOptionInfoTitle')}
        </h2>
        <PricePolicy
          isIncludeVat={isIncludeVat}
          isAnesthesiaCostIncluded={isAnesthesiaCostIncluded}
          isFollowUpServiceCostIncluded={isFollowUpServiceCostIncluded}
        />
      </FlexCol>
      <FlexRow px={12} py={10} className="bg-blue-100 text-blue-800 typo-body1Medium">
        {/* 시술에 대한 정보는 참고 사항이며, 병원 방문 시 개인의 상태에 따라 달라질 수 있어요. */}
        {t('pages.eventDetail.eventDetailOptionStandardizedCallout')}
      </FlexRow>
      <FlexCol gap={24}>
        {options.map((option, index) => (
          <OptionCard key={option.id} number={index + 1} option={option} />
        ))}
      </FlexCol>
    </FlexCol>
  )
}

const PricePolicy = ({
  isIncludeVat,
  isAnesthesiaCostIncluded,
  isFollowUpServiceCostIncluded,
}: {
  isIncludeVat: boolean
  isAnesthesiaCostIncluded: boolean
  isFollowUpServiceCostIncluded: boolean
}) => {
  const { t } = useTranslation()
  const containingText = t('pages.eventDetail.eventDetailPriceInfoIncluded')
  const notContainingText = t('pages.eventDetail.eventDetailPriceInfoNone')

  return (
    <FlexCol gap={8}>
      <FlexRow gap={8}>
        <IcCheck />
        <span className="typo-subTitle1Regular">
          {/* VAT */}
          {t('pages.eventDetail.eventDetailPriceInfoVAT')}
        </span>
        <span className="typo-subTitle1Bold">{isIncludeVat ? containingText : notContainingText}</span>
      </FlexRow>
      <FlexRow gap={8}>
        <IcCheck />
        <span className="typo-subTitle1Regular">
          {/* 마취비 */}
          {t('pages.eventDetail.eventDetailPriceInfoAnesthesiaFee')}
        </span>
        <span className="typo-subTitle1Bold">{isAnesthesiaCostIncluded ? containingText : notContainingText}</span>
      </FlexRow>
      <FlexRow gap={8}>
        <IcCheck />
        <span className="typo-subTitle1Regular">
          {/* 사후관리비 */}
          {t('pages.eventDetail.eventDetailPriceInfoFollowupFee')}
        </span>
        <span className="typo-subTitle1Bold">{isFollowUpServiceCostIncluded ? containingText : notContainingText}</span>
      </FlexRow>
    </FlexCol>
  )
}

const RecommendedFor = () => {
  const {
    serviceOffer: { procedureRecommended },
  } = useDetailServiceOffer()
  const { t } = useTranslation()

  if (!procedureRecommended) return null

  const descriptions = procedureRecommended.items.map((it) => it.description)

  return (
    <FlexCol gap={16}>
      <h2 className="typo-subTitle3Bold text-typography-primary">
        {/* 이런 분에게 추천해요 */}
        {t('pages.eventDetail.eventDetailProcedureRecommendedTitle')}
      </h2>
      <FlexCol gap={8}>
        {descriptions.map((description) => (
          <FlexRow key={description} items="start" gap={8}>
            <div className="w-6 h-6 rounded-full flex items-center justify-center">
              <IcCheck width={16} height={16} />
            </div>
            <span className="typo-subTitle1Regular text-typography-primary">{description}</span>
          </FlexRow>
        ))}
      </FlexCol>
    </FlexCol>
  )
}

const TreatmentProcess = () => {
  const {
    serviceOffer: { procedureProcess },
  } = useDetailServiceOffer()
  const { t, i18n } = useTranslation()

  if (!procedureProcess) return null

  const { totalProcedureMinutes, items } = procedureProcess

  return (
    <FlexCol gap={16}>
      <FlexRow elementType="h2" className="typo-subTitle3Bold text-typography-primary" gap={8} items="center">
        <span>
          {/* 시술 과정 */}
          {t('pages.eventDetail.eventDetailProcedureProcessTitle')}
        </span>
        {totalProcedureMinutes && (
          <>
            <span className="typo-subTitle3Bold text-typography-primary">:</span>
            <span className="typo-subTitle3Medium text-typography-primary">
              {getProcedureMinutes(totalProcedureMinutes, i18n.language as Locales)}
            </span>
          </>
        )}
      </FlexRow>
      <FlexCol elementType="ul" gap={8}>
        {items.map((it, index) => (
          <FlexRow key={it.description} elementType="li" items="center" gap={8}>
            <span
              className={cn(
                'typo-body1Regular text-typography-primary',
                'w-6 h-6 rounded-full flex items-center justify-center',
                'text-typography-description bg-gray-200',
              )}
            >
              {index + 1}
            </span>
            <p className="typo-subTitle1Regular text-typography-primary">{it.description}</p>
          </FlexRow>
        ))}
      </FlexCol>
    </FlexCol>
  )
}

const TreatmentDowntime = () => {
  const {
    serviceOffer: { downtime },
  } = useDetailServiceOffer()
  const { t, i18n } = useTranslation()

  if (!downtime) return null

  return (
    <FlexCol gap={16}>
      <FlexRow elementType="h2" gap={8} className="typo-subTitle3Bold text-typography-primary" items="center">
        <span className="typo-subTitle3Bold text-typography-primary">
          {/* 다운타임 */}
          {t('pages.eventDetail.eventDetailDowntimeTitle')}
        </span>
        {downtime?.value && (
          <>
            <span className="typo-subTitle3Bold text-typography-primary">:</span>
            <span className="typo-subTitle3Medium text-typography-primary">
              {getDowntimeContent(downtime, i18n.language as Locales)}
            </span>
          </>
        )}
      </FlexRow>
      <FlexRow>
        {/* 시술이나 수술 후의 휴식기, 즉 사회활동이나 일상생활에 지장을 받을 수 있는 기간 */}
        {t('pages.eventDetail.eventDetailDowntimeDescription')}
      </FlexRow>
    </FlexCol>
  )
}

const SideEffects = () => {
  const {
    serviceOffer: { sideEffects },
  } = useDetailServiceOffer()
  const { t } = useTranslation()

  if (!sideEffects) return null

  return (
    <FlexCol gap={16}>
      <h2 className="typo-subTitle3Bold text-typography-primary">
        {/* 부작용 안내 */}
        {t('pages.eventDetail.eventDetailSideEffect')}
      </h2>
      <pre className="whitespace-pre-wrap break-words">{sideEffects?.description}</pre>
    </FlexCol>
  )
}

const DescriptionImages = () => {
  const [isToggleOpened, setIsToggleOpened] = useState(false)
  const {
    serviceOffer: { id, descriptionImages, description },
  } = useDetailServiceOffer()
  const { t } = useTranslation()

  if (!descriptionImages || descriptionImages.length === 0) return null

  const trackClickShowMoreImage = () => {
    postAmplitude('click_more_event_image', {
      event_id: id,
    })
  }

  return (
    <FlexCol gap={16} pt={24} className={cn('relative bg-white')}>
      <h2 className="typo-subTitle3Bold text-typography-primary px-5">
        {/* 상세 정보 */}
        {t('pages.eventDetail.eventDetailDetailImageTitle')}
      </h2>
      <FlexCol className={cn('pb-6', isToggleOpened ? '' : 'h-[600px] overflow-hidden shadow-lg shadow-white')}>
        {descriptionImages.map((it) => (
          <Image
            key={it.url}
            src={it.url}
            width={100}
            height={100}
            alt={description || ''}
            className="w-full h-auto cursor-pointer object-cover"
          />
        ))}
      </FlexCol>
      {!isToggleOpened && (
        <FlexCol
          className={cn('absolute bottom-0 left-0 w-full')}
          items="center"
          justify="center"
          onClick={() => setIsToggleOpened(true)}
        >
          <div
            className="w-full h-[200px] bg-white"
            style={{
              background: 'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFF 100%)',
            }}
          />

          <FlexRow px={16} pb={24} className="bg-white w-full" pt={16}>
            <button
              className="w-full px-4 py-[14px] bg-gray-200 rounded-lg"
              onClick={() => {
                trackClickShowMoreImage()
              }}
            >
              <span className="typo-subTitle1Bold text-typography-primary">
                {/* 이미지 더보기 */}
                {t('pages.eventDetail.eventDetailShowMoreImageButton')}
              </span>
            </button>
          </FlexRow>
        </FlexCol>
      )}
    </FlexCol>
  )
}

const LocationMap = () => {
  const { locale, language } = useLocales()
  const {
    serviceOffer: { id, hospital },
  } = useDetailServiceOffer()

  if (!hospital) return null

  const lat = hospital.location?.latitude
  const lng = hospital.location?.longitude

  const address =
    hospital.location?.addresses.find((it) => (it.language as Languages) === language)?.address ||
    hospital.location?.addresses.find((it) => (it.language as Languages) === Languages.EN)?.address ||
    hospital.location?.addresses[0].address

  const trackClickHospital = () => {
    postAmplitude('click_hospital_info', {
      button_position: 'event-bottom',
      event_id: id,
      hospital_id: hospital.id,
      hospital_name: hospital.name || '',
      hospital_country: hospital.country || '',
    })
  }

  return (
    <FlexCol className="bg-white px-5 pt-6 pb-8" gap={16}>
      <Link href={`/hospitals/${hospital.id}`} onClick={trackClickHospital}>
        <FlexRow items="center" gap={2}>
          <h2 className="typo-subTitle3Bold text-typography-primary">{hospital.name}</h2>
          <IcArrowRight />
        </FlexRow>
      </Link>
      {lat && lng && (
        <div className="w-full h-full">
          <Image
            className="w-full h-full cursor-pointer object-cover position-absolute top-0 left-0 rounded-lg"
            src={getStaticGoogleMapURL({ lat, lng, width: 400, height: 200, locale })}
            width={400}
            height={200}
            onClick={() => window.open(`https://www.google.com/maps?q=${lat},${lng}`)}
            alt={`${hospital.name} location`}
          />
        </div>
      )}
      {address && (
        <FlexRow>
          <span className="typo-body1Regular text-typography-primary">{address}</span>
        </FlexRow>
      )}
    </FlexCol>
  )
}

const CTAButtons = () => {
  const router = useRouter()
  const { t } = useTranslation()
  const { locale, countryPathnamePrefix } = useLocales()
  const [isLoginConfirmVisible, setIsLoginConfirmVisible] = useState(false)
  const { serviceOffer, user } = useDetailServiceOffer()

  const isAnonymousUser = !user || user.id === 0

  const isKorean = locale === Locales.KO_KR

  const semanticAttributes = {
    type: 'EVENT',
    currency: serviceOffer.price.currency,
    products: [
      {
        productID: serviceOffer.id,
        name: serviceOffer.title,
        brandID: serviceOffer.hospital.id,
        brandName: serviceOffer.hospital.name,
        price: serviceOffer.price.discountedAmount,
        currency: serviceOffer.price.currency,
      },
    ],
  }

  return (
    <>
      <BottomBarInfoButtons
        aria-label="consultation information footer"
        ariaLabelOfButtonsWrapper="consultation button wrapper"
        buttonProps={
          isKorean
            ? [
                {
                  label: '상담받기',
                  onClick: () => {
                    postAmplitude('click_consultation', {
                      is_logged_in: !isAnonymousUser,
                      event_id: serviceOffer.id,
                      event_title: serviceOffer.title,
                      hospital_id: serviceOffer.hospital.id,
                      hospital_name: serviceOffer.hospital.name,
                    })
                    if (isAnonymousUser) {
                      setIsLoginConfirmVisible(true)
                    } else {
                      sendAirbridgeEvent(AirbridgeEvents.CONVERSION_BUTTON_CLICK, {
                        semanticAttributes,
                      })
                      // legacy. 추후 click_consultation로 통합합니다. _modn
                      postAmplitude('click_consultation_in_event_detail', {
                        event_id: serviceOffer.id,
                        event_title: serviceOffer.title,
                        hospital_id: serviceOffer.hospital.id,
                        hospital_name: serviceOffer.hospital.name,
                        hot_rating: serviceOffer.rating,
                        means: '일반신청',
                        operation_type: serviceOffer.operationType,
                      })

                      router.push({
                        pathname: `/events/${serviceOffer.id}/inquiry`,
                      })
                    }
                  },
                },
              ]
            : [
                {
                  label: t('pages.eventDetail.reserveThroughApp'),
                  onClick: () => {
                    postGtag('click_download')
                    postAmplitude('click_download')
                    // legacy. 추후 click_download로 통합합니다. _modn
                    postAmplitude('click_download_in_event_consultation_button', {
                      cost: serviceOffer.price.discountedAmount,
                      event_id: serviceOffer.id,
                      event_title: serviceOffer.title,
                      hospital_id: serviceOffer.hospital.id,
                      hospital_name: serviceOffer.hospital.name,
                      hot_rating: serviceOffer.rating,
                      means: '일반신청',
                      operation_type: serviceOffer.operationType,
                    })
                    sendAirbridgeEvent(AirbridgeEvents.APP_DOWNLOAD_BUTTON_CLICK, { semanticAttributes })
                    const link = getDownloadLink()
                    window.open(link, '_blank')
                  },
                },
              ]
        }
      />
      <ConfirmPopup
        isVisible={isLoginConfirmVisible}
        text={t('pages.eventDetail.contents.signinConfirmPopup', { returnObjects: true })}
        onConfirm={() => {
          router.push(createSignInUrl(`/events/${serviceOffer.id}`, locale))
        }}
        onClickBackground={() => {
          setIsLoginConfirmVisible(false)
        }}
        onCancel={() => {
          setIsLoginConfirmVisible(false)
        }}
      />
    </>
  )
}

const Divider = ({ mt, mb, my }: { mt?: number; mb?: number; my?: number }) => {
  return (
    <div
      className={'h-[1px] w-full bg-gray-300'}
      style={{
        marginTop: mt || my,
        marginBottom: mb || my,
      }}
    />
  )
}

const Ellipse = ({ className }: { className?: string }) => {
  return <div className={cn('w-[3px] h-[3px] bg-gray-600 rounded-full', className)} />
}

const getDowntimeContent = (downtime: { value: number; type: TimeUnit } | null, language: Locales): string => {
  try {
    if (!downtime) {
      return ''
    }
    if (language === Locales.TH_TH) {
      const thTimeTypeMap = {
        [TimeUnit.HOUR]: 'ชั่วโมง',
        [TimeUnit.DAY]: 'วัน',
        [TimeUnit.WEEK]: 'สัปดาห์',
        [TimeUnit.MONTH]: 'เดือน',
        [TimeUnit.YEAR]: 'ปี',
      }

      return `${downtime.value}${downtime.type ? thTimeTypeMap[downtime.type] : ''}`
    }
    if (language === Locales.EN_US) {
      const enTimeTypeMap = {
        [TimeUnit.HOUR]: 'hour(s)',
        [TimeUnit.DAY]: 'day(s)',
        [TimeUnit.WEEK]: 'week(s)',
        [TimeUnit.MONTH]: 'month(s)',
        [TimeUnit.YEAR]: 'year(s)',
      }

      return `${downtime.value}${downtime.type ? ' ' + enTimeTypeMap[downtime.type] : ''}`
    }
    if (language === Locales.JA_JP) {
      const jpTimeTypeMap = {
        [TimeUnit.HOUR]: '時間',
        [TimeUnit.DAY]: '日',
        [TimeUnit.WEEK]: '週間',
        [TimeUnit.MONTH]: 'ヶ月',
        [TimeUnit.YEAR]: '年',
      }
      return `${downtime.value}${downtime.type ? jpTimeTypeMap[downtime.type] : ''}`
    }

    const timeTypeMap = {
      [TimeUnit.HOUR]: '시간',
      [TimeUnit.DAY]: '일',
      [TimeUnit.WEEK]: '주',
      [TimeUnit.MONTH]: '월',
      [TimeUnit.YEAR]: '년',
    }

    return `${downtime.value}${downtime.type ? timeTypeMap[downtime.type] : ''}`
  } catch (error) {
    console.error('getDowntimeContent: Failed to get downtime content', error)
  }
  return ''
}

const getProcedureMinutes = (procedureMinutes: number | null, language: Locales): string => {
  if (!procedureMinutes) {
    return ''
  }
  if (language === Locales.TH_TH) {
    return `${procedureMinutes} นาที`
  }
  if (language === Locales.EN_US) {
    return `${procedureMinutes} minute(s)`
  }
  if (language === Locales.JA_JP) {
    return `${procedureMinutes}分`
  }
  return `${procedureMinutes}분`
}

ServiceOfferTemplate.CardImage = CardImage
ServiceOfferTemplate.MetaInfoWithRating = MetaInfoWithRating
ServiceOfferTemplate.SelectedOption = SelectedOption
ServiceOfferTemplate.Price = Price
ServiceOfferTemplate.PointRewards = PointRewards
ServiceOfferTemplate.SurgicalRewards = SurgicalRewards
ServiceOfferTemplate.ExclusivePrice = ExclusivePrice
ServiceOfferTemplate.EventPeriod = EventPeriod
ServiceOfferTemplate.HospitalInfo = HospitalInfo
ServiceOfferTemplate.Review = Review
ServiceOfferTemplate.Description = Description
ServiceOfferTemplate.Options = Options
ServiceOfferTemplate.RecommendedFor = RecommendedFor
ServiceOfferTemplate.TreatmentProcess = TreatmentProcess
ServiceOfferTemplate.TreatmentDowntime = TreatmentDowntime
ServiceOfferTemplate.SideEffects = SideEffects
ServiceOfferTemplate.DescriptionImages = DescriptionImages
ServiceOfferTemplate.LocationMap = LocationMap
ServiceOfferTemplate.CTAButtons = CTAButtons
